import { omit, pickBy, identity, isEmpty, isArray, isPlainObject, camelCase, snakeCase } from "lodash-es";
import Cookies from "universal-cookie";
import * as Sentry from "@sentry/react";
import queryString from "query-string";
const byteToHex = [];
for (let i = 0; i < 256; ++i) {
  byteToHex.push((i + 256).toString(16).slice(1));
}
function unsafeStringify(arr, offset = 0) {
  return (byteToHex[arr[offset + 0]] + byteToHex[arr[offset + 1]] + byteToHex[arr[offset + 2]] + byteToHex[arr[offset + 3]] + "-" + byteToHex[arr[offset + 4]] + byteToHex[arr[offset + 5]] + "-" + byteToHex[arr[offset + 6]] + byteToHex[arr[offset + 7]] + "-" + byteToHex[arr[offset + 8]] + byteToHex[arr[offset + 9]] + "-" + byteToHex[arr[offset + 10]] + byteToHex[arr[offset + 11]] + byteToHex[arr[offset + 12]] + byteToHex[arr[offset + 13]] + byteToHex[arr[offset + 14]] + byteToHex[arr[offset + 15]]).toLowerCase();
}
let getRandomValues;
const rnds8 = new Uint8Array(16);
function rng() {
  if (!getRandomValues) {
    if (typeof crypto === "undefined" || !crypto.getRandomValues) {
      throw new Error("crypto.getRandomValues() not supported. See https://github.com/uuidjs/uuid#getrandomvalues-not-supported");
    }
    getRandomValues = crypto.getRandomValues.bind(crypto);
  }
  return getRandomValues(rnds8);
}
const randomUUID = typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID.bind(crypto);
const native = { randomUUID };
function v4(options, buf, offset) {
  if (native.randomUUID && true && !options) {
    return native.randomUUID();
  }
  options = options || {};
  const rnds = options.random ?? options.rng?.() ?? rng();
  if (rnds.length < 16) {
    throw new Error("Random bytes length must be >= 16");
  }
  rnds[6] = rnds[6] & 15 | 64;
  rnds[8] = rnds[8] & 63 | 128;
  return unsafeStringify(rnds);
}
var Environment = /* @__PURE__ */ ((Environment2) => {
  Environment2["LOCAL"] = "local";
  Environment2["STAGING"] = "staging";
  Environment2["TESTING"] = "testing";
  Environment2["CYPRESS_TEST"] = "cypress_test";
  Environment2["EPHEMERAL"] = "ephemeral";
  Environment2["PRODUCTION"] = "production";
  Environment2["UNKNOWN"] = "unknown";
  return Environment2;
})(Environment || {});
const LOCAL_HOSTNAMES = ["localhost", "127.0.0.1"];
const STAGING_HOSTNAMES = [
  "staging.color.com",
  "home.staging.color.com",
  "home.use1-a.eks.staging.color.com",
  "clinical.staging.color.com",
  "genomics-stb.joinallofus.org",
  "gp-staging.use1-a.eks.staging.color.com",
  "rhp-staging.use1-a.eks.staging.color.com",
  "aou-pdf.use1-a.eks.staging.color.com",
  "color-hub.use1-a.eks.staging.color.com",
  "hub.staging.color.com"
];
const TESTING_HOSTNAMES = ["gp-testing.use1-a.eks.staging.color.com"];
const PRODUCTION_HOSTNAMES = [
  "home.color.com",
  "home.use1-a.eks.color.com",
  "clinical.color.com",
  "genomics.joinallofus.org",
  "joinallofus.org",
  "rhp.aou.color.com",
  "aou-pdf.use1-a.eks.color.com",
  "hub.color.com"
];
function isEnvironmentLocal() {
  return LOCAL_HOSTNAMES.includes(window.location.hostname);
}
function isEnvironmentStaging() {
  return STAGING_HOSTNAMES.includes(window.location.hostname);
}
function isEnvironmentTesting() {
  return TESTING_HOSTNAMES.includes(window.location.hostname);
}
function isEnvironmentProduction() {
  return PRODUCTION_HOSTNAMES.includes(window.location.hostname);
}
function isEnvironmentEphemeral() {
  return window.location.hostname.startsWith("ephemeral-") && window.location.hostname.endsWith(".use1-a.eks.color.dev");
}
function isEnvironmentLocalAndDevelopment() {
  return isEnvironmentLocal() && window.__ENV__ === "development";
}
function isEnvironmentCypressTest() {
  return isEnvironmentLocal() && window.Cypress !== void 0;
}
function getEnvironment() {
  if (isEnvironmentCypressTest()) {
    return Environment.CYPRESS_TEST;
  }
  if (isEnvironmentLocal()) {
    return Environment.LOCAL;
  }
  if (isEnvironmentStaging()) {
    return Environment.STAGING;
  }
  if (isEnvironmentTesting()) {
    return Environment.TESTING;
  }
  if (isEnvironmentProduction()) {
    return Environment.PRODUCTION;
  }
  if (isEnvironmentEphemeral()) {
    return Environment.EPHEMERAL;
  }
  return Environment.UNKNOWN;
}
function getEnvironmentOrDefault(defaultEnv) {
  if (isEnvironmentLocal()) {
    return Environment.LOCAL;
  }
  if (isEnvironmentStaging()) {
    return Environment.STAGING;
  }
  if (isEnvironmentTesting()) {
    return Environment.TESTING;
  }
  if (isEnvironmentProduction()) {
    return Environment.PRODUCTION;
  }
  return defaultEnv;
}
class ApiError extends Error {
  status;
  details;
  constructor(errorPayload) {
    super(errorPayload.message);
    this.name = "API Response Error";
    this.status = errorPayload.status;
    this.details = omit(errorPayload, ["message", "status"]);
  }
}
function captureException(error, otherProps) {
  Sentry.captureException(error, { level: "error", ...otherProps });
}
function captureMessage(message, otherProps) {
  Sentry.captureMessage(message, { level: "info", ...otherProps });
}
const loggerUtils = {
  logMessageToConsole(message, ...optionalParameters) {
    console.log(message, ...optionalParameters);
  },
  logToConsole(error, extra) {
    console.error(error, extra);
  },
  logException(error, otherProps) {
    console.error(error, otherProps);
    if (!(error instanceof ApiError)) {
      captureException(error, {
        ...otherProps
      });
    }
  },
  logMessage(error, otherProps) {
    console.log(error, otherProps);
    captureMessage(error.message, {
      ...otherProps
    });
  }
};
var EventType = /* @__PURE__ */ ((EventType2) => {
  EventType2["IDENTIFY"] = "identify";
  EventType2["PAGE"] = "page";
  EventType2["RESET"] = "reset";
  EventType2["TRACK"] = "track";
  return EventType2;
})(EventType || {});
const ANONYMOUS_IP_ADDRESS = "0.0.0.0";
const cookies = new Cookies();
const internalState = {
  sessionStart: -1,
  sessionDeviceId: ""
};
const getDeviceId = () => {
  const getDeviceIdFromLocalStorage = (localStorage2) => {
    const existingDeviceId = localStorage2.getItem("amplitudeDeviceId");
    if (existingDeviceId) {
      return existingDeviceId;
    }
    const newDeviceId = String(v4());
    localStorage2.setItem("amplitudeDeviceId", newDeviceId);
    return newDeviceId;
  };
  const getDeviceIdFromMemory = () => {
    if (!internalState.sessionDeviceId) {
      internalState.sessionDeviceId = String(v4());
    }
    return internalState.sessionDeviceId;
  };
  let localStorage;
  try {
    localStorage = window.localStorage;
  } catch {
  }
  if (localStorage) {
    return getDeviceIdFromLocalStorage(localStorage);
  }
  return getDeviceIdFromMemory();
};
const getSegmentSessionId = () => {
  if (internalState.sessionStart < 0) {
    internalState.sessionStart = Math.round(Date.now());
  }
  return internalState.sessionStart;
};
const getMarketingCookies = () => {
  const data = pickBy(
    {
      source: cookies.get("utm_source"),
      medium: cookies.get("utm_medium"),
      term: cookies.get("utm_term"),
      content: cookies.get("utm_content"),
      name: cookies.get("utm_campaign")
    },
    identity
  );
  return isEmpty(data) ? void 0 : data;
};
const getSegmentPersistentTraits = () => {
  if (typeof window.analytics === "undefined") {
    return void 0;
  }
  if (typeof window.analytics.user !== "function") {
    return void 0;
  }
  const user = window.analytics.user();
  if (typeof user.traits !== "function") {
    return void 0;
  }
  return user.traits();
};
const getBrazeId = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const brazeId = urlParams.get("bid");
  if (brazeId) {
    return brazeId;
  }
  const traits = getSegmentPersistentTraits();
  if (!traits) {
    return void 0;
  }
  return traits.braze_id;
};
const getTraitsForContext = () => {
  const brazeId = getBrazeId();
  if (brazeId) {
    return {
      braze_id: brazeId
    };
  }
  return void 0;
};
const getSegmentContext = () => {
  return pickBy(
    {
      campaign: getMarketingCookies(),
      ip: ANONYMOUS_IP_ADDRESS,
      device: {
        id: getDeviceId()
      },
      traits: getTraitsForContext()
    },
    identity
  );
};
const sendCommand = (command, ...args) => {
  if (isEnvironmentLocalAndDevelopment()) {
    loggerUtils.logMessageToConsole("segment:", command, ...args);
  }
  if (typeof window.analytics !== "undefined") {
    window.analytics[command](...args);
  }
};
const getSegmentProperties = (properties) => {
  return {
    ...properties,
    color_session_id: getSegmentSessionId()
  };
};
const identifySegmentUser = (props) => {
  const { isColorEmployee = false, patientId, properties } = props;
  const brazeId = getBrazeId();
  const segmentProperties = {
    ...getSegmentProperties(properties),
    ...brazeId ? { braze_id: brazeId } : {}
  };
  sendCommand(
    /* command */
    EventType.IDENTIFY,
    /* userId */
    patientId,
    /* traits */
    {
      is_color_employee: isColorEmployee,
      ...segmentProperties
    },
    /* options */
    { context: getSegmentContext() }
  );
};
const resetSegmentUser = () => {
  sendCommand(EventType.RESET, {});
  internalState.sessionStart = -1;
  internalState.sessionDeviceId = "";
};
const logSegmentPageView = (name, properties) => {
  sendCommand(
    /* command */
    EventType.PAGE,
    /* category */
    void 0,
    /* name */
    name,
    /* properties */
    getSegmentProperties(properties),
    /* options */
    {
      context: getSegmentContext()
    }
  );
};
const trackSegmentEvent = (event, properties = {}) => {
  sendCommand(
    /* command */
    EventType.TRACK,
    /* event */
    event,
    /* properties */
    getSegmentProperties(properties),
    /* options */
    {
      context: getSegmentContext()
    }
  );
};
function appendQueryParams(url, params) {
  if (!params || isEmpty(params)) {
    return url;
  }
  const query = queryString.stringify(params);
  return query ? `${url}?${query}` : url;
}
function getQueryParam(search, propName, options = {}) {
  const { index = 0 } = options;
  const params = queryString.parse(search);
  const props = params[propName];
  if (props == null) {
    return void 0;
  }
  return isArray(props) ? props[index] ?? void 0 : props;
}
function getQueryParams(search, propName) {
  const params = queryString.parse(search);
  const props = params[propName];
  if (props == null) {
    return [];
  }
  return isArray(props) ? props : [props];
}
const UNSAFE_REDIRECT_LIST = [
  // Anything looking like a URL, like `http://evil.org` or even ``//evil.org`
  /\/\//,
  // Catchall for http
  /^http/,
  // Scripts
  /javascript/,
  /data:/,
  /<.*script:/,
  /[\r\n]/,
  // See https://getcolor.atlassian.net/browse/VUL-47
  // Case: http://localhost:8888/sign-in?next=\/evil.org  `\evil.org` (with the leading backslash)
  /\\/
];
function getSafeUrl(nextUrl) {
  if (/^http.?:\/\/(home|www|providers)?\.?color.com/.test(nextUrl)) {
    return nextUrl;
  }
  if (UNSAFE_REDIRECT_LIST.some((regex) => regex.test(nextUrl))) {
    return void 0;
  }
  return nextUrl;
}
function getSafeNextQueryParam(search) {
  const nextUrl = getQueryParam(search, "next");
  if (!nextUrl) {
    return void 0;
  }
  const additionalParams = queryString.parse(search.split(nextUrl)[1] ?? "");
  return getSafeUrl(appendQueryParams(nextUrl, additionalParams));
}
function getSafeVerifyEmailNextUrlQueryParam(search) {
  const verifyEmailNextUrl = getQueryParam(search, "verifyEmailNextUrl");
  if (!verifyEmailNextUrl) {
    return void 0;
  }
  const additionalParams = queryString.parse(search.split(verifyEmailNextUrl)[1] ?? "");
  return getSafeUrl(appendQueryParams(verifyEmailNextUrl, additionalParams));
}
function toQueryString(params) {
  return `?${queryString.stringify(params)}`;
}
function stripQueryParam(search, propName) {
  const remainder = queryString.exclude(search, [propName]);
  return remainder.length > 0 ? remainder : "?";
}
function mergeQueryParams(search, updatedParams) {
  return toQueryString({
    ...queryString.parse(search),
    ...updatedParams
  });
}
function mergeQueryParam(search, propName, propValue) {
  return mergeQueryParams(search, { [propName]: propValue });
}
function getCurrentPath() {
  const { pathname, search } = window.location;
  return `${pathname}${search}`;
}
function getNextSearchString(nextUrl) {
  return mergeQueryParam("", "next", nextUrl ?? getCurrentPath());
}
function trimTrailingSlash(path) {
  return path.replace(/\/$/, "");
}
function addErrorMessage(error) {
  const { status, message, ...otherProps } = error;
  const uniqueValues = new Set(Object.values(otherProps).flat(Infinity));
  const displayMessage = message ?? Array.from(uniqueValues).join(", ");
  return {
    status,
    message: displayMessage,
    ...otherProps
  };
}
function getError(error) {
  if (error instanceof Error) {
    return error;
  }
  if (error instanceof Object) {
    return new Error(JSON.stringify(error));
  }
  return new Error(String(error));
}
function convertObjectKeys(object, convertKey) {
  if (isPlainObject(object)) {
    return Object.keys(object).reduce(
      (result, key) => {
        result[convertKey(key)] = convertObjectKeys(object[key], convertKey);
        return result;
      },
      {}
    );
  }
  if (isArray(object)) {
    return object.map((value) => convertObjectKeys(value, convertKey));
  }
  return object;
}
function convertObjectKeysToCamelCase(object) {
  return convertObjectKeys(object, camelCase);
}
function convertObjectKeysToSnakeCase(object) {
  return convertObjectKeys(object, snakeCase);
}
function getCsrfCookieName() {
  if (isEnvironmentProduction()) {
    return "csrftoken_prod";
  }
  if (isEnvironmentStaging()) {
    return "csrftoken_staging";
  }
  if (isEnvironmentEphemeral()) {
    return "csrftoken_ephemeral";
  }
  return "csrftoken";
}
const COOKIES = new Cookies();
const ENABLE_SLOW_API = isEnvironmentLocal() && false;
const NON_LOGGABLE_HTTP_ERROR_STATUSES = /* @__PURE__ */ new Set([401]);
const X_CSRF_TOKEN_HEADER_KEY = "X-CSRFToken";
const X_SEGMENT_SESSION_ID_HEADER_KEY = "X-Segment-Session-Id";
function addDefaultFetchOptions(fetchOptions, url) {
  const headers = { ...fetchOptions.headers };
  const csrfCookieName = getCsrfCookieName();
  if (!("X-CSRFToken" in headers) && COOKIES.get(csrfCookieName)) {
    headers[X_CSRF_TOKEN_HEADER_KEY] = COOKIES.get(csrfCookieName);
  }
  if (!("X-Segment-Session-Id" in headers)) {
    headers[X_SEGMENT_SESSION_ID_HEADER_KEY] = getSegmentSessionId();
  }
  let includeCredentials = isEnvironmentLocalAndDevelopment();
  const { origin } = window.location;
  let destination = "";
  try {
    destination = new URL(url).origin;
  } catch {
  }
  if (origin.match(/.*hub.*\.color\.(com|dev)$/) && destination.match(/.*[./]color\.(com|dev)$/)) {
    includeCredentials = true;
  }
  return {
    ...fetchOptions,
    headers,
    credentials: includeCredentials ? "include" : "same-origin"
  };
}
class APIClient {
  /**
   * A fetch wrapper, with an API similar to `requests` in Python.
   *
   * Helper methods which include the method are provided below.
   *
   * @param method - The HTTP request method.
   * @param url - The url to make a request to.
   * @param options - Additional information about this request.
   */
  request = (method, url, options = {}) => {
    let fetchHeaders = options.headers;
    const { params = {}, json = {} } = options;
    const urlWithQueryParams = appendQueryParams(url, params);
    let body;
    const methodIsGet = method === "GET";
    if (!isEmpty(json)) {
      if (!methodIsGet) {
        body = json;
      }
      fetchHeaders = {
        // FIXME - `Content-Type` doesn't make sense for `GET` requests.
        "Content-Type": "application/json",
        // FIXME - `Accept` is unrelated to whether we are sending a JSON object.
        // Most likely all our API's should specify this, since our endpoints can
        // only really handle JSON (not HTML)
        Accept: "application/json",
        ...fetchHeaders
      };
    }
    const fetchOptions = {
      method,
      body,
      headers: fetchHeaders
    };
    return this.colorFetch(urlWithQueryParams, fetchOptions);
  };
  get = (url, options = {}) => {
    return this.request("GET", url, options);
  };
  post = (url, options = {}) => {
    return this.request("POST", url, options);
  };
  postFormData = (url, options = {}) => {
    return this.colorFetch(url, { ...options, method: "POST" });
  };
  put = (url, options = {}) => {
    return this.request("PUT", url, options);
  };
  delete = (url, options = {}) => {
    return this.request("DELETE", url, options);
  };
  patch = (url, options = {}) => {
    return this.request("PATCH", url, options);
  };
  uploadFile = (url, options) => {
    const { params = {}, headers, method = "POST", file } = options;
    const urlWithQueryParams = appendQueryParams(url, params);
    const fetchOptions = {
      method,
      body: file,
      headers: {
        // This is necessary to receive a response in json format
        // Otherwise you will get this error: 'renderer returned unicode, and did not specify'
        Accept: "application/json",
        ...headers
      }
    };
    return this.doFetch(urlWithQueryParams, addDefaultFetchOptions(fetchOptions, url));
  };
  /**
   * A fetch wrapper.
   *
   * @param url - The url to make a request against
   * @param options - The fetch options
   */
  colorFetch = (url, options) => {
    let fetchBody = options.body;
    if (typeof fetchBody === "object" && !(fetchBody instanceof FormData)) {
      fetchBody = JSON.stringify(fetchBody);
    }
    return this.doFetch(
      url,
      addDefaultFetchOptions(
        {
          ...options,
          body: fetchBody
        },
        url
      )
    );
  };
  // To fix this we'd have to make this all methods in this class static.
  // eslint-disable-next-line class-methods-use-this
  doFetch = async (url, options) => {
    const UNSPECIFIED_SERVER_ERROR = {
      message: "Failed to process request.",
      status: 500
    };
    let response;
    try {
      if (ENABLE_SLOW_API) ;
      response = await fetch(url, options);
    } catch (error) {
      loggerUtils.logToConsole(getError(error));
      return Promise.reject(new ApiError(UNSPECIFIED_SERVER_ERROR));
    }
    try {
      const contentType = response.headers.get("Content-Type") || response.headers.get("content-type") || "";
      const isJsonContentType = contentType.includes("application/json");
      const { ok: responseIsOk, status } = response;
      if (!responseIsOk) {
        let responseContent = UNSPECIFIED_SERVER_ERROR;
        if (isJsonContentType) {
          const errorResponse = await response.json();
          if (typeof errorResponse === "object") {
            responseContent = addErrorMessage(errorResponse);
          } else if (typeof errorResponse === "string") {
            responseContent.message = errorResponse;
          }
        } else if (contentType.includes("application/text")) {
          responseContent.message = await response.text();
        }
        responseContent.status = status;
        loggerUtils.logToConsole(
          new Error(`Server responded with ${status} for URL: ${url}`),
          responseContent
        );
        const apiError = new ApiError(responseContent);
        if (status >= 400 && !NON_LOGGABLE_HTTP_ERROR_STATUSES.has(status)) {
          loggerUtils.logException(apiError);
        }
        return Promise.reject(apiError);
      }
      if (isJsonContentType) {
        return response.json();
      }
      return null;
    } catch (error) {
      loggerUtils.logException(getError(error));
      return Promise.reject(new ApiError(UNSPECIFIED_SERVER_ERROR));
    }
  };
}
const ApiClient = new APIClient();
export {
  ApiClient as A,
  getQueryParams as B,
  getSafeUrl as C,
  getSafeVerifyEmailNextUrlQueryParam as D,
  Environment as E,
  mergeQueryParams as F,
  trimTrailingSlash as G,
  toQueryString as a,
  isEnvironmentLocal as b,
  isEnvironmentStaging as c,
  isEnvironmentProduction as d,
  loggerUtils as e,
  getEnvironment as f,
  getQueryParam as g,
  appendQueryParams as h,
  identifySegmentUser as i,
  ApiError as j,
  getSafeNextQueryParam as k,
  logSegmentPageView as l,
  mergeQueryParam as m,
  getCurrentPath as n,
  getError as o,
  getCsrfCookieName as p,
  convertObjectKeysToCamelCase as q,
  resetSegmentUser as r,
  stripQueryParam as s,
  trackSegmentEvent as t,
  convertObjectKeysToSnakeCase as u,
  isEnvironmentTesting as v,
  isEnvironmentLocalAndDevelopment as w,
  isEnvironmentCypressTest as x,
  getEnvironmentOrDefault as y,
  getNextSearchString as z
};
//# sourceMappingURL=api-DijU5Gmf.js.map
